'use client';

import { useRef, useState } from 'react';
import type { ComponentType, KeyboardEvent } from 'react';
import Link from 'next/link';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import ShowcaseDeviceFrame from './ShowcaseDeviceFrame';
import IndividualLivingRecordPreview from './IndividualLivingRecordPreview';
import FamilyLedgerPreview from './FamilyLedgerPreview';
import SocialStorytellingPreview from './SocialStorytellingPreview';
import TrustRecordPreview from './TrustRecordPreview';
import InstitutionOverviewPreview from './InstitutionOverviewPreview';
import { SHOWCASE_TABS, type ShowcaseTabId } from './showcase-data';

const PREVIEW_COMPONENTS: Record<ShowcaseTabId, ComponentType> = {
  individual: IndividualLivingRecordPreview,
  family: FamilyLedgerPreview,
  social: SocialStorytellingPreview,
  trust: TrustRecordPreview,
  institution: InstitutionOverviewPreview,
};

export default function ProductShowcaseSection() {
  const [activeTab, setActiveTab] = useState<ShowcaseTabId>('individual');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const activeIndex = SHOWCASE_TABS.findIndex((tab) => tab.id === activeTab);
  const active = SHOWCASE_TABS[activeIndex];
  const Preview = PREVIEW_COMPONENTS[active.id];

  function selectTab(index: number, focus = false) {
    const nextIndex = (index + SHOWCASE_TABS.length) % SHOWCASE_TABS.length;
    setActiveTab(SHOWCASE_TABS[nextIndex].id);
    if (focus) tabRefs.current[nextIndex]?.focus();
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab(index + 1, true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab(index - 1, true);
    } else if (event.key === 'Home') {
      event.preventDefault();
      selectTab(0, true);
    } else if (event.key === 'End') {
      event.preventDefault();
      selectTab(SHOWCASE_TABS.length - 1, true);
    }
  }

  return (
    <section
      id="product-showcase"
      tabIndex={-1}
      className="relative w-full min-w-0 max-w-full overflow-hidden bg-[var(--public-bg)] px-4 py-16 focus:outline-none sm:px-6 sm:py-20 lg:px-8 lg:py-24"
    >
      <div className="mx-auto w-full min-w-0 max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className="text-balance text-3xl text-slate-950 sm:text-4xl"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            See how a living record comes together
          </h2>
          <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">
            Explore how stories, memories, relationships, journeys, recognition and institutional
            records connect inside Ertzyx.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Product showcase by experience"
          className="scrollbar-none -mx-4 mt-10 flex snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain px-4 py-1 scroll-px-4 sm:mx-0 sm:justify-center sm:overflow-visible sm:px-0"
        >
          {SHOWCASE_TABS.map((tab, index) => {
            const selected = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                id={`showcase-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="showcase-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => selectTab(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                className={`shrink-0 snap-start rounded-[9px] border px-4 py-2.5 text-sm font-bold transition-[background-color,border-color,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d9e75] focus-visible:ring-offset-2 ${
                  selected
                    ? 'border-transparent bg-[#0f6e56] text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                }`}
              >
                {tab.navLabel}
              </button>
            );
          })}
        </div>

        <div
          id="showcase-panel"
          role="tabpanel"
          aria-labelledby={`showcase-tab-${active.id}`}
          className="mt-10"
        >
          <div
            key={active.id}
            className="grid min-w-0 grid-cols-1 items-start gap-8 motion-safe:animate-fade-in lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-12"
          >
            <ShowcaseDeviceFrame label={active.frameLabel}>
              <Preview />
            </ShowcaseDeviceFrame>

            <div className="min-w-0">
              <h3
                className="text-2xl leading-tight text-slate-950 sm:text-3xl"
                style={{ fontFamily: 'DM Serif Display, serif' }}
              >
                {active.heading}
              </h3>
              <p className="mt-4 text-base leading-7 text-slate-600">{active.copy}</p>

              <ul className="mt-6 space-y-3">
                {active.features.map((feature) => (
                  <li key={feature.label} className="flex items-start gap-2.5">
                    <CheckCircle2
                      size={17}
                      className="mt-0.5 shrink-0"
                      style={{ color: active.accent }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 text-sm leading-6 text-slate-700">
                      <span className="font-bold text-slate-900">{feature.label}.</span>{' '}
                      {feature.description}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                href={active.ctaHref}
                className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-[9px] bg-[#0f6e56] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#0c5c48] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d9e75] focus-visible:ring-offset-2 active:translate-y-px"
              >
                {active.ctaLabel}
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
